import React, { useEffect, useState } from 'react';
import { getSmsHistoryBySmsId } from '../../services/SmsService';

function SmsHistorySidebar({ isOpen, onClose, sms }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sms?.id) {
      fetchHistory();
    }
  }, [isOpen, sms?.id]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getSmsHistoryBySmsId(sms.id);
      setHistory(data || []);
    } catch (error) {
      console.error('Failed to fetch SMS history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-success';
      case 'sent':
      case 'processed':
      case 'created':
        return 'bg-secondary';
      case 'failed':
      case 'bounce':
      case 'deferred':
        return 'bg-danger';
      case 'undelivered':
        return 'bg-warning text-dark';
      default:
        return 'bg-dark text-white';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Sidebar */}
      <div
        className="offcanvas offcanvas-end show"
        style={{ width: '500px', zIndex: 1045 }}
        tabIndex="-1"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">
            <i className="bi bi-clock-history me-2"></i>
            SMS History
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {sms && (
            <div className="mb-4">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">SMS Details</h6>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Recipient</small>
                      <p className="mb-2">{sms.recipientNumber}</p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Current Status</small>
                      <p className="mb-2">
                        <span className={`badge ${getStatusBadgeClass(sms.lastStatus)}`}>
                          {sms.lastStatus || 'Unknown'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <small className="text-muted">Message</small>
                      <p className="mb-2" style={{ wordBreak: 'break-word' }}>
                        {sms.body}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Sent Date</small>
                      <p className="mb-0">
                        {new Date(sms.createdAt).toLocaleDateString()} {new Date(sms.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Twilio SID</small>
                      <p className="mb-0 text-break">{sms.twilioMessageSid || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <h6 className="mb-3">Status History</h6>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
              <p className="mt-2">No history available</p>
            </div>
          ) : (
            <div style={{
              position: 'relative',
              paddingLeft: '30px'
            }}>
              <div style={{
                content: '',
                position: 'absolute',
                left: '15px',
                top: 0,
                bottom: 0,
                width: '2px',
                background: '#e9ecef'
              }}></div>
              
              {history.map((entry, index) => (
                <div key={index} style={{
                  position: 'relative',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '-22px',
                    top: 0
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 2px #e9ecef',
                      backgroundColor: getStatusBadgeClass(entry.status).includes('success') ? '#28a745' :
                                    getStatusBadgeClass(entry.status).includes('secondary') ? '#6c757d' :
                                    getStatusBadgeClass(entry.status).includes('danger') ? '#dc3545' :
                                    getStatusBadgeClass(entry.status).includes('warning') ? '#ffc107' :
                                    getStatusBadgeClass(entry.status).includes('dark') ? '#343a40' : '#6c757d'
                    }}></div>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    borderLeft: '3px solid #dee2e6'
                  }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{entry.status || 'Unknown Status'}</h6>
                        {entry.description && (
                          <p className="text-muted mb-1 small">{entry.description}</p>
                        )}
                        {entry.errorCode && (
                          <p className="text-danger mb-1 small">Error: {entry?.errorCode}. {entry?.errorMessage}</p>
                        )}
                      </div>
                      <small className="text-muted">
                        {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString()}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SmsHistorySidebar; 